import * as store from '../app/store.js';
import { Scenario, ScenarioSummary } from '../domain/types.js';
import { formatCompactRub, formatTerm } from './format.js';
import { initImportDialog } from './import-dialog.js';
import { initShareDialog, openShareDialog } from './share-dialog.js';
import { calendarPaymentDate } from '../domain/bank-calendar.js';

const DEFAULT_SCENARIO: Omit<Scenario, 'id' | 'name' | 'startDate'> = {
  principal: 15_500_000_00, // 15.5M * 100 cents
  termMonths: 360,
  rateApr: 16.9,
  repaymentType: 'fixed',
  ratePeriods: [],
  overpayments: [],
  refinances: [],
  overpaymentStrategy: 'reduce_term',
  calculationMode: 'bank_calendar',
};

const DEFAULT_PAYMENT_DAY = 8;

/** «Сценарий 2», «Сценарий 3» … — one more than the scenarios already in the store. */
export function nextScenarioName(): string {
  return `Сценарий ${store.getState().scenarios.length + 1}`;
}

/** A fresh plain calendar loan (no events) starting this month, added to the store and selected. */
export function createScenario(name: string): Scenario {
  const startDate = new Date().toISOString().substring(0, 7); // YYYY-MM
  const scenario: Scenario = {
    ...DEFAULT_SCENARIO,
    id: crypto.randomUUID(),
    name,
    startDate,
    // Disbursed a month before the first payment, on the same day.
    // Half-way recalculation, as in the built-in example (main.ts, sampleRecalculation).
    bankCalendar: {
      disbursementDate: calendarPaymentDate(startDate, -1, DEFAULT_PAYMENT_DAY),
      paymentDay: DEFAULT_PAYMENT_DAY,
      recalculationMonths: [DEFAULT_SCENARIO.termMonths / 2],
    },
  };
  store.upsertScenario(scenario); // notify → main.ts populates the form
  return scenario;
}

/** Copies scenario `id` under `name`, adds the copy to the store and selects it; `null` for an unknown id. */
export function duplicateScenario(id: string, name: string): Scenario | null {
  const source = store.getState().scenarios.find(s => s.id === id);
  if (!source) return null;
  const copy: Scenario = { ...structuredClone(source), id: crypto.randomUUID(), name };
  store.upsertScenario(copy);
  return copy;
}

function button(className: string, text: string, title?: string): HTMLButtonElement {
  const node = document.createElement('button');
  node.type = 'button';
  node.className = `action-btn ${className}`;
  node.textContent = text;
  if (title) node.title = title;
  return node;
}

function chip(text: string, className: string): HTMLSpanElement {
  const node = document.createElement('span');
  node.className = `scenario-chip ${className}`;
  node.textContent = text;
  return node;
}

/** «7 лет · 2 мес. · выплатите 25,5 млн ₽ · переплата 10 млн ₽» */
export function cardMeta(summary: ScenarioSummary): string {
  return `${formatTerm(summary.actualTermMonths)} · выплатите ${formatCompactRub(summary.totalPaid)} · переплата ${formatCompactRub(summary.totalInterest)}`;
}

/**
 * The drawer is a modal dialog: while closed it sits off-screen, so it is `inert`
 * (out of the tab order and the accessibility tree); opening moves focus to the close
 * button, closing returns it to the element that opened the drawer, Escape closes.
 */
export class ScenarioList {
  private drawer: HTMLElement;
  private backdrop: HTMLElement;
  private listContainer: HTMLElement;
  private closeButton: HTMLElement | null;
  private opener: HTMLElement | null = null;
  private isOpen = false;
  private unsubscribe: () => void;

  constructor(drawerId: string, backdropId: string, listId: string) {
    this.drawer = document.getElementById(drawerId)!;
    this.backdrop = document.getElementById(backdropId)!;
    this.listContainer = document.getElementById(listId)!;
    this.closeButton = document.getElementById('drawer-close');

    this.setInert(true);
    this.bindEvents();
    initShareDialog();
    initImportDialog(() => this.close());
    this.unsubscribe = store.subscribe(() => this.render());
    this.render(); // Initial render
  }

  public open() {
    if (this.isOpen) return;
    this.isOpen = true;
    const active = document.activeElement;
    this.opener = active instanceof HTMLElement && active !== document.body ? active : null;
    this.setInert(false);
    this.drawer.classList.add('open');
    this.backdrop.classList.add('open');
    this.closeButton?.focus();
  }

  public close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.drawer.classList.remove('open');
    this.backdrop.classList.remove('open');
    // Focus leaves the drawer before it turns inert; otherwise the browser drops it on <body>.
    const target = this.opener ?? document.getElementById('open-scenarios');
    this.opener = null;
    if (target instanceof HTMLElement && target.isConnected) target.focus();
    this.setInert(true);
  }

  private setInert(inert: boolean) {
    if (inert) {
      this.drawer.setAttribute('inert', '');
      this.drawer.setAttribute('aria-hidden', 'true');
    } else {
      this.drawer.removeAttribute('inert');
      this.drawer.removeAttribute('aria-hidden');
    }
  }

  private bindEvents() {
    this.backdrop.addEventListener('click', () => this.close());
    this.closeButton?.addEventListener('click', () => this.close());
    document.getElementById('new-scenario-btn')?.addEventListener('click', () => createScenario(nextScenarioName()));
    document.addEventListener('keydown', event => {
      if (event.key !== 'Escape' || !this.isOpen) return;
      // A native modal on top (share / import) answers Escape itself; preventing the default
      // here would cancel its close request and shut the drawer underneath instead.
      if (document.querySelector('dialog[open]')) return;
      event.preventDefault();
      this.close();
    });
  }

  private render() {
    const summaries = store.listSummaries();
    const state = store.getState();
    const activeId = state.lastViewedId;
    const baselineId = state.baselineId;

    const comparisonIds = state.comparisonIds;
    const focus = this.focusedAction();
    this.listContainer.replaceChildren(
      ...summaries.map(s => this.renderCard(s, {
        isActive: s.scenarioId === activeId,
        isBaseline: s.scenarioId === baselineId,
        isCompared: comparisonIds.includes(s.scenarioId),
        compareFull: comparisonIds.length >= store.MAX_COMPARISON,
      })),
    );
    // Re-rendering replaces the cards: put focus back on the same button (a keyboard user
    // toggles several cards in a row); otherwise it would land on <body>.
    this.restoreFocus(focus);
    if (this.isOpen && !this.drawer.contains(document.activeElement)) this.closeButton?.focus();

    const trigger = document.getElementById('open-scenarios');
    if (trigger) trigger.textContent = `Сценарии · ${summaries.length}`;
  }

  /** Card id and action-button position that hold focus, so a re-render can put it back. */
  private focusedAction(): { id: string; index: number } | null {
    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || !active.classList.contains('action-btn')) return null;
    const card = active.closest<HTMLElement>('.scenario-card');
    if (!card?.dataset.id) return null;
    return { id: card.dataset.id, index: Array.from(card.querySelectorAll('.action-btn')).indexOf(active) };
  }

  private restoreFocus(focus: { id: string; index: number } | null) {
    if (!focus) return;
    const card = Array.from(this.listContainer.querySelectorAll<HTMLElement>('.scenario-card')).find(c => c.dataset.id === focus.id);
    const button = card?.querySelectorAll<HTMLButtonElement>('.action-btn')[focus.index];
    if (button && !button.disabled) button.focus();
  }

  // Every data value goes through textContent: scenario names are user input.
  private renderCard(
    summary: ScenarioSummary,
    flags: { isActive: boolean; isBaseline: boolean; isCompared: boolean; compareFull: boolean },
  ): HTMLElement {
    const { isActive, isBaseline, isCompared, compareFull } = flags;
    const card = document.createElement('div');
    card.className = ['scenario-card', isActive ? 'active' : '', isBaseline ? 'baseline' : ''].filter(Boolean).join(' ');
    card.dataset.id = summary.scenarioId;

    const title = document.createElement('div');
    title.className = 'scenario-card-title';
    const name = document.createElement('h3');
    name.textContent = summary.name;
    title.append(name);
    if (isBaseline) title.append(chip('базовый', 'is-baseline'));
    if (isActive) title.append(chip('в форме', 'is-active'));
    if (isCompared) title.append(chip('в сравнении', 'is-compared'));

    const meta = document.createElement('p');
    meta.className = 'meta';
    meta.textContent = cardMeta(summary);

    const actions = document.createElement('div');
    actions.className = 'scenario-card-actions';
    const compare = button(
      isCompared ? 'toggle-compare is-on' : 'toggle-compare',
      isCompared ? 'В сравнении ✓' : '+ В сравнение',
      isCompared ? 'Убрать из сравнения' : undefined,
    );
    if (!isCompared && compareFull) {
      compare.disabled = true;
      compare.title = `Максимум ${store.MAX_COMPARISON} сценариев`;
    }
    actions.append(
      button('duplicate', 'Дублировать'),
      button('share', 'Ссылка', 'Ссылка и код для этого сценария'),
      isBaseline
        ? button('unset-baseline', 'Базовый ✓', 'Снять с базового')
        : button('set-baseline', 'Сделать базовым'),
      compare,
      button('delete', 'Удалить'),
    );

    card.append(title, meta, actions);
    card.addEventListener('click', event => {
      const target = event.target;
      const actionButton = target instanceof Element ? target.closest('.action-btn') : null;
      // A click on a disabled action button reaches the card without the button as its
      // target; the actions row never selects the card.
      if (!actionButton && target instanceof Element && target.closest('.scenario-card-actions')) return;
      if (actionButton?.classList.contains('duplicate')) {
        event.stopPropagation();
        duplicateScenario(summary.scenarioId, `${summary.name} (копия)`);
      } else if (actionButton?.classList.contains('share')) {
        event.stopPropagation();
        const scenario = store.getState().scenarios.find(s => s.id === summary.scenarioId);
        if (scenario) void openShareDialog(scenario);
      } else if (actionButton?.classList.contains('delete')) {
        event.stopPropagation();
        this.delete(summary.scenarioId, summary.name);
      } else if (actionButton?.classList.contains('set-baseline') || actionButton?.classList.contains('unset-baseline')) {
        event.stopPropagation();
        this.toggleBaseline(summary.scenarioId);
      } else if (actionButton?.classList.contains('toggle-compare')) {
        event.stopPropagation();
        store.toggleComparison(summary.scenarioId);
      } else {
        this.select(summary.scenarioId);
      }
    });
    return card;
  }

  private select(id: string) {
    store.selectScenario(id);
    this.close();
  }

  private toggleBaseline(id: string) {
    const state = store.getState();
    if (state.baselineId === id) {
      store.setBaseline(null);
    } else {
      store.setBaseline(id);
    }
  }

  private delete(id: string, name: string) {
    if (confirm(`Удалить сценарий «${name}»? Это нельзя отменить.`)) {
      store.removeScenario(id);
      if (store.getState().scenarios.length === 0) {
        createScenario(nextScenarioName());
      }
    }
  }
}
